import { useMutation, useQueryClient } from '@tanstack/react-query';

import { procurementApi } from 'api/procurement';

import { purchaseOrderQueryKeys } from '../queries/PurchaseOrderQueries';

/**
 * Bulk table actions for PO V2 list: send to vendor, PDF download, pre-populated CSV.
 * Network orchestration lives here; callers keep selection UX and pre-mutation validation.
 *
 * **Canonical bulk mutation shape** (each `useMutation` below is intentionally parallel — same
 * key order and same phases inside `onSuccess`):
 *
 * 1. `mutationFn` — async work; return structured data (send/PDF) or throw on failure (CSV).
 * 2. `onSuccess` — async:
 *    - **Phase A:** `if (shouldRefetchList) await invalidatePurchaseOrdersList()`
 *    - **Phase B:** `if (!onNotify) return`
 *    - **Phase C:** exactly one `onNotify` (success | warning | error summary)
 * 3. `onError` — `if (!onNotify) return` then one error `onNotify`.
 *
 * New bulk actions should copy this block structure.
 *
 * @param {Object} options
 * @param {(payload: { text: string, level: 'success'|'error'|'warning'|'info' }) => void} [options.onNotify]
 */
export function useBulkPurchaseOrderTableMutations({ onNotify } = {}) {
    const queryClient = useQueryClient();

    const invalidatePurchaseOrdersList = () => queryClient.invalidateQueries({ queryKey: purchaseOrderQueryKeys.all });

    const bulkSendToVendorMutation = useMutation({
        mutationFn: async ({ purchaseOrderIds }) => {
            const settled = await Promise.allSettled(
                purchaseOrderIds.map((id) => procurementApi.sendPurchaseOrderToVendor(id)),
            );
            const res = settled.map((s) => {
                if (s.status === 'fulfilled') {
                    return s.value;
                }
                const { reason } = s;
                const msg = reason instanceof Error ? reason.message : String(reason ?? 'Request failed.');
                return { status: false, msg };
            });
            const succeededPurchaseOrderIds = purchaseOrderIds.filter((_, i) => res[i]?.status);
            const allSucceeded = res.every((item) => item?.status);
            const firstFailed = res.find((r) => !r?.status);
            return {
                allSucceeded,
                firstFailedMsg: firstFailed?.msg,
                succeededPurchaseOrderIds,
                totalAttempted: purchaseOrderIds.length,
            };
        },
        onSuccess: async (data) => {
            // Phase A: refresh PO list
            if (data.succeededPurchaseOrderIds.length > 0) {
                await invalidatePurchaseOrdersList();
            }
            // Phase B: guard toast channel
            if (!onNotify) return;
            // Phase C: exactly one summary toast
            if (data.allSucceeded) {
                onNotify({ level: 'success', text: 'Sent PO(s) to Vendor(s)' });
            } else if (data.succeededPurchaseOrderIds.length > 0) {
                const sent = data.succeededPurchaseOrderIds.length;
                const { totalAttempted } = data;
                const detail = data.firstFailedMsg ? ` ${data.firstFailedMsg}` : '';
                onNotify({
                    level: 'warning',
                    text: `Sent ${sent} of ${totalAttempted} PO(s) to vendors; some could not be sent.${detail}`,
                });
            } else {
                const msg = data.firstFailedMsg
                    ? `Issue sending the selected POs to vendors. ${data.firstFailedMsg}`
                    : 'Issue sending the selected POs to vendors.';
                onNotify({ level: 'error', text: msg });
            }
        },
        onError: (e) => {
            if (!onNotify) return;
            const msg = e?.message
                ? `Failed to send purchase orders to vendors. ${e.message}`
                : 'Failed to send purchase orders to vendors.';
            onNotify({ level: 'error', text: msg });
        },
    });

    const bulkDownloadPdfsMutation = useMutation({
        mutationFn: async ({ pdfItems }) => {
            const settled = await Promise.allSettled(
                pdfItems.map(async ({ id, poRow }) => {
                    if (!poRow) {
                        return {
                            id,
                            success: false,
                            errorMsg: `Could not find data for PO ID ${id} to download PDF.`,
                        };
                    }
                    const result = await procurementApi.downloadPOPDFWithStatus(
                        poRow.id,
                        poRow.vendorName,
                        poRow.facilityName,
                    );
                    if (result?.status) {
                        return { id: poRow.id, success: true };
                    }
                    const errMsg = result?.msg
                        ? `Error downloading PDF for PO ${poRow.id}. ${result.msg}`
                        : `Error downloading PDF for PO ${poRow.id}.`;
                    return { id: poRow.id, success: false, errorMsg: errMsg };
                }),
            );
            const results = settled.map((s, i) => {
                if (s.status === 'fulfilled') {
                    return s.value;
                }
                const { id } = pdfItems[i];
                const { reason } = s;
                const errText = reason instanceof Error ? reason.message : String(reason ?? 'Request failed.');
                return {
                    id,
                    success: false,
                    errorMsg: `Error downloading PDF for PO ${id}. ${errText}`,
                };
            });
            return { results };
        },
        onSuccess: async (data) => {
            const { results } = data;
            let successCount = 0;
            let errorCount = 0;
            results.forEach((r) => {
                if (r.success) successCount += 1;
                else errorCount += 1;
            });
            // Phase A: refresh PO list
            if (successCount > 0) {
                await invalidatePurchaseOrdersList();
            }
            // Phase B: guard toast channel
            if (!onNotify) return;
            // Phase C: exactly one summary toast
            const firstFailedResult = results.find((r) => !r.success);
            const firstFailureDetail = firstFailedResult?.errorMsg ? ` ${firstFailedResult.errorMsg}` : '';
            if (successCount > 0 && errorCount === 0) {
                onNotify({
                    level: 'success',
                    text: `Successfully initiated download for ${successCount} PDF(s).`,
                });
            } else if (successCount > 0 && errorCount > 0) {
                onNotify({
                    level: 'warning',
                    text: `Initiated download for ${successCount} PDF(s). Failed to initiate for ${errorCount} PDF(s).${firstFailureDetail}`,
                });
            } else if (errorCount > 0 && successCount === 0) {
                onNotify({
                    level: 'error',
                    text: `Failed to initiate download for ${errorCount} PDF(s).${firstFailureDetail}`,
                });
            }
        },
        onError: (e) => {
            if (!onNotify) return;
            const msg = e?.message ? `Failed to download PDFs. ${e.message}` : 'Failed to download PDFs.';
            onNotify({ level: 'error', text: msg });
        },
    });

    const bulkDownloadCsvMutation = useMutation({
        mutationFn: async ({ purchaseOrderIds }) => {
            const result = await procurementApi.downloadPrePopulatedCsvV2(purchaseOrderIds);
            if (!result?.status) {
                throw new Error(result?.msg ?? 'Failed to download CSV.');
            }
        },
        onSuccess: async () => {
            // Phase A: refresh PO list
            await invalidatePurchaseOrdersList();
            // Phase B: guard toast channel
            if (!onNotify) return;
            // Phase C: exactly one summary toast
            onNotify({
                level: 'success',
                text: 'Successfully initiated download for pre-populated CSV.',
            });
        },
        onError: (e) => {
            if (!onNotify) return;
            const msg = e?.message ? `Failed download csv file: ${e.message}` : 'Failed download csv file.';
            onNotify({ level: 'error', text: msg });
        },
    });

    return {
        bulkSendToVendorMutation,
        bulkDownloadPdfsMutation,
        bulkDownloadCsvMutation,
    };
}

useBulkPurchaseOrderTableMutations.displayName = 'useBulkPurchaseOrderTableMutations';
